import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { Button, Card, Input } from "../design-system/components";
import { type AuthError, signInEmail, signInSocial, signUpEmail } from "../lib/auth";
import { useSession } from "../lib/session";

export function LoginPage() {
  const { user, loading, refresh } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  if (!loading && user) return <Navigate to={from} replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      if (mode === "signup") await signUpEmail(name, email, password);
      else await signInEmail(email, password);
      await refresh();
      navigate(from, { replace: true });
    } catch (err) {
      setError((err as AuthError).message ?? "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pr-login">
      <Card className="pr-login__card">
        <div className="pr-login__brand">
          <img src="/play-rough-mark-pink.svg" alt="" />
          <div>
            <div style={{ fontFamily: "var(--font-heading)", fontWeight: 700, fontSize: 22 }}>
              Play Rough
            </div>
            <div className="pr-muted" style={{ fontSize: 13 }}>
              Battle Log
            </div>
          </div>
          <p className="pr-muted" style={{ margin: 0, fontSize: 14 }}>
            Sign in with your Play Rough Rankings account.
          </p>
        </div>
        <form className="pr-form" onSubmit={submit}>
          {mode === "signup" && (
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
            />
          )}
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
          />
          {error && <div className="pr-alert pr-alert--error">{error}</div>}
          <Button type="submit" fullWidth disabled={busy}>
            {busy ? "…" : mode === "signup" ? "Create account" : "Sign in"}
          </Button>
          <div className="pr-or">or</div>
          <div className="pr-inline" style={{ justifyContent: "center" }}>
            <Button
              variant="outline"
              onClick={() =>
                signInSocial("google").catch((err: AuthError) => setError(err.message))
              }
            >
              Google
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                signInSocial("discord").catch((err: AuthError) => setError(err.message))
              }
            >
              Discord
            </Button>
          </div>
          <Button variant="link" onClick={() => setMode(mode === "signin" ? "signup" : "signin")}>
            {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
          </Button>
        </form>
      </Card>
    </div>
  );
}
